import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import { connect } from 'react-redux';
import { Input } from 'grab-ui';
import { deliverySelector } from 'selectors/new-booking-selectors';
import NewBookingDuck from 'ducks/new-booking-duck';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import ScribeDuck from 'ducks/scribe-duck';
import _debounce from 'lodash/debounce';
import OfferTermCondition from './offer-term-condition';
import OfferListComponent from './offer-list-component';
import PromoCodeInputComponent from './promo-code-input-component';
import OfferListFooter from './offer-list-footer';
import OverlayBody from '../overlay-body';
import styles from './styles.less';
import OfferPanelContext from './offer-panel-context';
import { useHandleScribeOffers } from '../../common/scribe-offers-common';
import { getOfferID } from '../../../../../helper/anytime-booking';

const OfferListOverLay = ({ onClose, dispatch, delivery }) => {
  const [offerTC, setOfferTC] = useState(null);
  const [showOfferDetail, setShowOfferDetail] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const fetchingPromo = _get(delivery, 'fetchingPromo');
  const offerConfig = _get(delivery, 'detail.configuration.offerConfig');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const selectedOfferID = getOfferID(selectedOffer);
  const offerList = _get(delivery, 'detail.offerList', []);

  useEffect(() => {
    // After open overlay
    dispatch(
      ScribeDuck.creators.scribeExpressReviewOfferListDefault(offerConfig),
    );
  }, []);

  useEffect(
    () => {
      setSelectedOffer(null);
      if (offerConfig) {
        const selectedOfferID = getOfferID(offerConfig);
        if (selectedOfferID) {
          const offer = offerList.find(offer => {
            const iterateOfferID = getOfferID(offer);
            return iterateOfferID === selectedOfferID;
          });
          if (offer) {
            setSelectedOffer(offer);
          }
        }
      }
    },
    [offerList, offerConfig],
  );

  const onCloseOverlay = useCallback(
    () => {
      setSelectedOffer(null);
      onClose();
      dispatch(
        ScribeDuck.creators.scribeExpressReviewOfferListBack(offerConfig),
      );
    },
    [dispatch, delivery, onClose, offerConfig],
  );

  const handleViewOfferDetail = useCallback(
    offerID => () => {
      const chooseOffer = offerList.find(
        offer => getOfferID(offer) === offerID,
      );

      if (chooseOffer) {
        setOfferTC(chooseOffer);
        setShowOfferDetail(true);
        dispatch(
          ScribeDuck.creators.scribeExpressReviewOfferListCheckOfferDetails(
            offerConfig,
            offerID,
          ),
        );
      }
    },
    [dispatch, offerConfig, offerList],
  );

  const onUseOffer = useCallback(
    offerID => () => {
      const offer = offerList.find(offer => offer.offerID === offerID);
      if (offer) {
        setSelectedOffer(offer);
      }
      onCloseOfferDetail();
    },
    [dispatch, offerList, delivery, onCloseOfferDetail],
  );

  const onRemoveOffer = useCallback(
    () => {
      setSelectedOffer(null);
      onCloseOfferDetail();
    },
    [dispatch, delivery, onCloseOfferDetail],
  );

  const onCloseOfferDetail = useCallback(
    () => {
      setShowOfferDetail(false);
      setOfferTC(null);
    },
    [setShowOfferDetail, setOfferTC],
  );

  const onPromoCodeFocus = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.updateOfferList(delivery.id, []));
      setSelectedOffer(null);
      setShowCodeInput(true);
      dispatch(
        ScribeDuck.creators.scribeExpressReviewOfferListEnterPromoCode(
          offerConfig,
        ),
      );
    },
    [setShowCodeInput, dispatch, delivery, offerConfig],
  );

  const onClosePromoCodeInput = useCallback(
    () => {
      setShowCodeInput(false);
      dispatch(NewBookingDuck.creators.fetchOfferList(delivery.id));
      setSelectedOffer(offerConfig);
    },
    [setShowCodeInput, dispatch, delivery, offerConfig],
  );

  const setOfferConfig = useCallback(
    offer => {
      dispatch(NewBookingDuck.creators.setOfferConfig(delivery.id, offer));
    },
    [dispatch, delivery],
  );

  const handleSelectOffer = useCallback(
    offerID => {
      if (selectedOffer && getOfferID(selectedOffer) === offerID) {
        setSelectedOffer(null);
        return;
      }
      const offer = offerList.find(offer => getOfferID(offer) === offerID);
      if (offer) {
        setSelectedOffer(offer);
      }
    },
    [dispatch, offerList, delivery, selectedOffer],
  );

  const onSearchTextChange = useCallback(
    _debounce(promoCode => {
      dispatch(NewBookingDuck.creators.inputPromoCode(delivery.id, promoCode));
    }, 300),
    [dispatch, delivery],
  );

  const {
    handleScribeSelectOffer,
    handleScribeOfferDetailDefault,
    handleScribeApplyOffer,
    handleScribeContinueWithoutOffer,
    handleScribeOfferDetailBack,
    handleScribeOfferDetailUseOffer,
    handleScribeEnterCodeDetailsBack,
    handleScribeEnterCodeDetailsTypeCode,
    handleScribeEnterCodeDetailsShowErrorResult,
  } = useHandleScribeOffers(offerConfig);

  const errorMessage = useMemo(
    () => {
      const paxExpressError = _get(delivery, 'paxExpressError');
      const discountEligibilityError = _get(
        offerConfig,
        'discountEligibilityError',
        null,
      );
      if (paxExpressError || discountEligibilityError) {
        return (
          _get(paxExpressError, 'message') ||
          _get(discountEligibilityError, 'localizedMessage') ||
          I18n.t('v2.error.invalidPromo')
        );
      }
      return null;
    },
    [delivery, offerConfig],
  );

  const offerPanelContext = useMemo(
    () => ({
      onViewOfferDetail: handleViewOfferDetail,
      handleSelectOffer,
      handleScribeSelectOffer,
      handleScribeApplyOffer,
      handleScribeContinueWithoutOffer,
      handleScribeOfferDetailDefault,
      handleScribeOfferDetailBack,
      handleScribeOfferDetailUseOffer,
      handleScribeEnterCodeDetailsBack,
      handleScribeEnterCodeDetailsTypeCode,
      handleScribeEnterCodeDetailsShowErrorResult,
      setOfferConfig,
      errorMessage,
    }),
    [handleViewOfferDetail, handleSelectOffer, setOfferConfig, errorMessage],
  );

  if (showOfferDetail) {
    return (
      <OfferPanelContext.Provider value={offerPanelContext}>
        <OfferTermCondition
          offer={offerTC}
          selectedOfferID={selectedOfferID}
          onClose={onCloseOfferDetail}
          onSelectOffer={onUseOffer(offerTC.offerID)}
          onRemoveOffer={onRemoveOffer}
        />
      </OfferPanelContext.Provider>
    );
  }

  if (showCodeInput) {
    return (
      <OfferPanelContext.Provider value={offerPanelContext}>
        <PromoCodeInputComponent
          delivery={delivery}
          offerList={offerList}
          fetchingPromo={fetchingPromo}
          selectedOffer={selectedOffer}
          offerConfig={offerConfig}
          onClose={onClosePromoCodeInput}
          onCloseOverlay={onClose}
          onUseOffer={onUseOffer}
          onRemoveOffer={onRemoveOffer}
          onSearchTextChange={onSearchTextChange}
        />
      </OfferPanelContext.Provider>
    );
  }

  return (
    <OfferPanelContext.Provider value={offerPanelContext}>
      <OverlayBody
        title={I18n.t('phrase.offers.mktplace_offers_body_item_offers')}
        showHeader
        showClose
        showTitle
        onClose={onCloseOverlay}
        showFooter
        showConfirm={false}
        footer={
          <OfferListFooter
            onClose={onClose}
            offerList={offerList}
            selectedOffer={selectedOffer}
            offerConfig={offerConfig}
          />
        }
        classnames={{
          footer: styles.footer,
        }}
      >
        <div className={styles.promoListContainer}>
          <Input
            placeholder={I18n.t(
              'phrase.offers.MKTPLACE_OFFERS_ENTER_PROMO_CODE',
            )}
            onFocus={onPromoCodeFocus}
          />
          <OfferListComponent
            offerList={offerList}
            selectedOffer={selectedOffer}
            offerConfig={offerConfig}
            fetchingPromo={fetchingPromo}
          />
        </div>
      </OverlayBody>
    </OfferPanelContext.Provider>
  );
};

OfferListOverLay.defaultProps = {
  onClose: undefined,
};

OfferListOverLay.propTypes = {
  dispatch: PropTypes.func.isRequired,
  delivery: NewBookingDeliveryPropType.isRequired,
  onClose: PropTypes.func,
};

export default connect((state, props) => ({
  delivery: deliverySelector(state, props),
}))(OfferListOverLay);
